import { Dialog } from 'primereact/dialog'
import React, { useContext, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { isEmpty } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import { InventoryMoveScanDialogProps, LocationData, MoveGoodsData } from '../../types/inventory'
import { InputText } from 'primereact/inputtext'
import { tokens } from 'locales/tokens'
import { useTranslation } from 'react-i18next'

const InventoryMoveScanDialog = (props: InventoryMoveScanDialogProps) => {
    const { $toast, $http, $alert } = useContext(CommonContext)
    const { t } = useTranslation()
    const [lctSeq, setLctSeq] = useState<string>()
    const [goods, setGoods] = useState<MoveGoodsData>({
        invnMvSeq: 0,
        invnMvProgCd: '',
        invnMvProgNm: '',
        invnMvTypCd: '',
        invnMvTypNm: '',
        invnMvYmd: '',
        lctSeq: '',
        invnSeq: 0,
        goodsId: '',
        goodsNm: '',
        specTxt: '',
        gdslsUnitCd: '',
        goodsTmprCd: '',
        goodsTmprNm: '',
        srcLctPathNm: '',
        dprCenterId: '',
        dprCenterNm: '',
        arvCenterId: '',
        arvCenterNm: '',
        shlfYmd: '',
        mvGoodsQty: 0,
        prchUnitAmt: 0,
        lotNo: '',
        locationAlias: '',
    })

    useMountEffect(() => {
        if (!props.visible) return
        if (!props.data) return
        setGoods(props.data)
        console.log('useMountEffect-goods', props.data)
    })

    const moveInventory = async () => {
        if (!goods.invnMvSeq) return

        if (isEmpty(lctSeq)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.stock_movement),
                text: t(tokens.inventory.please_scan_moving_location_code_or_barcode),
            })
            return
        }

        const rLoc = await $http().get('/inbound/location-info', { params: { barcode: lctSeq } })
        if (!rLoc) return
        const loc = rLoc.location
        if (loc === null) {
            $alert({
                icon: 'error',
                title: t(tokens.inventory.location_barcode_error),
                text: t(tokens.inventory.location_barcode_information_is_incorrect),
            })
            return
        }
        if (loc.lctSeq === goods.lctSeq) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.stock_movement),
                text: t(tokens.inventory.same_location_departure_and_arrival),
            })
            return
        }
        if (loc.goodsTmprCd !== goods.goodsTmprCd) {
            // 이미 상품 기준 품온 확인
            $alert({
                icon: 'info',
                title: t(tokens.inventory.check_location_temperature),
                text: `${t(tokens.common.product_temperature)} [` + goods.goodsTmprNm + ']' + t(tokens.inventory.is_not_the_location_of),
            })
            return
        }
        // 반품존(RT)은 재고이동에서만 목적지가 될 수 있다.
        // 이 다이얼로그는 창간이동도 같이 쓰는데, 창간이동은 서버가 RT 를
        // 거부한다. 여기서 막지 않으면 스캔은 되고 확정에서 에러가 난다.
        const allowedLctTypCd =
            goods.invnMvTypCd === 'C' ? ['KP', 'PK'] : ['KP', 'PK', 'RT']

        if (!allowedLctTypCd.includes(loc.lctTypCd)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.check_location_usage),
                text: t(tokens.inventory.please_process_product_stacking_at_the_picking_storage_location) + '(' + loc.lctTypNm + ')',
            })
            return
        }
        if (
            goods.invnMvTypCd === 'C' && // 창간이동
            loc.centerId === goods.dprCenterId
        ) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.stock_movement),
                text: t(tokens.inventory.same_center_location),
            })
            return
        }

        const msgStr =
            `${t(tokens.inventory.location_to_move)}(` +
            lctSeq +
            `)${t(tokens.inventory.please_check_again)}\n\n` +
            loc.areaNm +
            '〉' +
            loc.sectorNm +
            '〉' +
            loc.bayId +
            '〉' +
            loc.rackRowId +
            t(tokens.common.row) +
            loc.rackColId +
            t(tokens.common.layer)
        const isConfirm = await $alert({
            icon: 'info',
            title: t(tokens.inventory.stock_movement),
            text: msgStr,
            isConfirm: true,
        })

        if (!isConfirm) return

        const data = {
            invnMvSeq: goods.invnMvSeq,
            mvLctSeq: lctSeq,
        }

        const result = await $http().put('/inventory/move-goods', data)
        if (result) {
            $toast({ title: t(tokens.common.success), text: t(tokens.inventory.complete_stock_movement), icon: 'success' })
            props.onHide(true)
        }
    }

    const onKeyDownSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            moveInventory()
        }
    }

    const cancelMoveInventory = async () => {
        if (!goods.invnMvSeq) return

        const isConfirm = await $alert({
            icon: 'info',
            title: t(tokens.common.inventory_transfer_cancellation),
            text: t(tokens.inventory.are_you_cancel_stock_movement_request),
            isConfirm: true,
        })

        if (!isConfirm) return

        const result = await $http().put('/inventory/cancel-move-goods', goods)
        if (result) {
            $toast({ title: t(tokens.common.success), text: t(tokens.inventory.canceled_stock_movement_request), icon: 'success' })
            props.onHide(true)
        }
    }

    const onDetected = (result: any) => {
        console.log('onDetected', result)
    }
    const header = (title: string) => {
        return <div className="p-0 text-center m-0">{title}</div>
    }

    return (
        <Dialog
            header={header(t(tokens.inventory.stock_movement))}
            headerStyle={{ padding: '12px' }}
            visible={props.visible}
            onHide={() => props.onHide(false)}
            closable={false}
            contentClassName="p-0"
            style={{ width: '60vh' }}
        >
            {props.visible && (
                <div className="grid">
                    <div className="col-11 mx-3">
                        <InputText
                            value={lctSeq}
                            className="p-inputtext-md w-full"
                            placeholder={t(tokens.common.enter_location_code_or_scan_barcode)}
                            onChange={(e) => setLctSeq(e.currentTarget.value)}
                            onKeyDown={(e) => onKeyDownSearch(e)}
                        />
                    </div>
                    <div className="col-12 flex p-0">
                        <div style={{ minHeight: '10px' }}></div>
                        {/* 2023-11-09 김덕중 PL: 
PDA기기에서 카메라 보다 일반적인 바코드 스캔해서 로케이션 입력이 더 편리하여서 
카메라 스캔 기능 제외
                    <Scanner
                        width={window.innerWidth}
                        height={250}
                        readers={["ean_reader", "code_128_reader"]}
                        start={scanner}
                        onStart={(e: boolean) => {
                            console.log('onStart', e)
                            setScanner(e)
                        }}
                        onDetected={(e: string) => {
                            onDetected(e)
                        }}
                    />
*/}
                    </div>
                    <div className="col-12 flex justify-content-between p-2">
                        <div className="col-6 flex">
                            <Button
                                className="w-full p-button-secondary font-bold"
                                label={t(tokens.common.close)}
                                size="small"
                                onClick={() => props.onHide(false)}
                            />
                        </div>
                        <div className="col-6 flex">
                            <Button
                                className="w-full p-button-warning font-bold"
                                label={t(tokens.common.inventory_movement_cancellation)}
                                size="small"
                                onClick={cancelMoveInventory}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    )
}

export default InventoryMoveScanDialog
